import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LabWithRanking } from 'models/order';
import type { Organization } from 'models/organization';
import { formatCurrency } from 'utils/format';
import { OrderBy, OrderDirection } from 'models/search-result';
import LabDetailsDialog from 'components/connect-to-lab/lab-details-dialog/lab-details-dialog.component';

type LabTableProps = {
    labs: LabWithRanking[];
    disableList?: boolean;
    selectedLabId: string | undefined;
    onLabSelected: (lab: LabWithRanking) => void;
    onSort: (orderBy: OrderBy) => void;
};
const LabTable: FC<LabTableProps> = ({ labs, disableList, selectedLabId, onLabSelected, onSort }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'rank',
        orderDirection: OrderDirection.desc,
    });
    const [labInfoDialogLab, setLabInfoDialogLab] = useState<LabWithRanking | null>(null);

    const sortDataBy = async (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
        onSort(orderBy);
    };

    return (
        <>
            <Paper variant="outlined">
                <Box m={1}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="1"></TableCell>
                                    <TableCell width="80"></TableCell>
                                    <TableCell>{translate('common:LAB')}</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            onClick={() => sortDataBy('rank')}
                                            active={orderBy.orderBy === 'rank'}
                                            direction={orderBy.orderDirection}
                                        >
                                            {translate('orders:RANK')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            onClick={() => sortDataBy('price')}
                                            active={orderBy.orderBy === 'price'}
                                            direction={orderBy.orderDirection}
                                        >
                                            {translate('orders:PRICE')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" width="180">
                                        <TableSortLabel
                                            onClick={() => sortDataBy('deliveryDate')}
                                            active={orderBy.orderBy === 'deliveryDate'}
                                            direction={orderBy.orderDirection}
                                        >
                                            {translate('orders:DELIVERY_DATE')}
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {labs.map((lab) => (
                                    <TableRow
                                        key={lab._id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => onLabSelected(lab)}
                                    >
                                        <TableCell>
                                            <Checkbox
                                                disabled={disableList}
                                                checked={selectedLabId === lab.labId}
                                                id={`lab-${lab._id}`}
                                                size="large"
                                                onChange={() => onLabSelected(lab)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <img
                                                src={lab.profileImage}
                                                loading="lazy"
                                                style={{ maxHeight: 40, maxWidth: 80 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                {lab.name}
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setLabInfoDialogLab(lab);
                                                    }}
                                                    aria-label={translate('orders:LAB_INFO')}
                                                >
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{lab.rank}</TableCell>
                                        <TableCell align="right">
                                            {(!lab.totalPrice || lab.totalPrice === 0) && lab.isQuotable
                                                ? translate('common:QUOTABLE')
                                                : formatCurrency(lab.totalPrice, lab.currency)}
                                        </TableCell>
                                        <TableCell align="right">{lab.deliveryDate.deliveryDate}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            <LabDetailsDialog
                open={Boolean(labInfoDialogLab)}
                onClose={() => setLabInfoDialogLab(null)}
                lab={
                    labInfoDialogLab
                        ? ({
                              ...labInfoDialogLab,
                              logoSignedUrl: labInfoDialogLab.profileImage,
                          } as unknown as Organization)
                        : undefined
                }
            />
        </>
    );
};

export default LabTable;
